import React, { useEffect } from 'react';
import { generatePath, Redirect, Route, useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import RemoteConfigGate from 'components/RemoteConfigGate';
import {
  REMOTE_CONFIG_KEYS,
  USER_ROLES,
  hasAnyAllowedRole,
  isPackedClaims,
  resolveOrgAccess,
  useAccountWritable,
} from 'packages/utils';

const AuthRoute = ({
  exact = false,
  path,
  location,
  component: Component,
  allowedRoles = null,
  onOrgChangeRedirectTo,
  remoteConfigKey,
  writeOnly = false,
}) => {
  const { orgId: paramOrgId } = useParams();
  const history = useHistory();
  const loadingAccess = useSelector(({ user }) => user.access.loading);
  const claims = useSelector(({ user }) => user.access.data?.claims);
  const orgUserLoaded = useSelector(({ user }) => user.orgUser?.loaded);
  const orgUserAccess = useSelector(({ user }) => user.orgUser.data?.access);
  const userOrgDivisions = useSelector(({ divisions }) => divisions?.assigned?.data?.divisions) || {};
  const selectedOrgId = useSelector(({ organizations }) => organizations?.organization?.id);

  // Packed-token fallback: the org user doc in redux belongs to the selected org, so it is
  // only a valid fallback when the route param points at that same org.
  const fallbackOrgAccess = paramOrgId === selectedOrgId ? orgUserAccess : undefined;
  const userOrgAccess = resolveOrgAccess(claims, paramOrgId, fallbackOrgAccess);

  const hasAccess = hasAnyAllowedRole(allowedRoles, userOrgAccess, userOrgDivisions);
  // Pure "create" pages close while the account is read-only. Edit routes stay open on purpose:
  // they double as detail views. Their save action must be hidden by the screen itself, through
  // a write `allowedRole` or `useAccountWritable()`: this route check ignores the account status.
  const accountWritable = useAccountWritable();

  useEffect(() => {
    if (onOrgChangeRedirectTo && selectedOrgId && paramOrgId !== selectedOrgId) {
      // Ideally this should only run when the ids are different, because the
      // selected org changed by the call of OrganizationSelector.
      const path = generatePath(onOrgChangeRedirectTo, { orgId: selectedOrgId });
      history.replace(path);
    }
  }, [selectedOrgId]);

  const renderRoute = routeProps => {
    // Wait for claims before mounting the screen or RemoteConfigGate so RC
    // cannot briefly show a gated view while roles are still loading.
    // Packed tokens have no org role until the org user document reloads after a
    // reset. Rendering nothing avoids sending the user to / during that read.
    if (loadingAccess || (isPackedClaims(claims) && orgUserLoaded === false)) {
      return null;
    }
    if (!hasAccess) {
      return <Redirect to="/" />;
    }
    if (writeOnly && !accountWritable) {
      const listPath =
        paramOrgId && onOrgChangeRedirectTo
          ? generatePath(onOrgChangeRedirectTo, { orgId: paramOrgId })
          : '/';
      return <Redirect to={listPath} />;
    }
    const content = <Component {...routeProps} />;
    if (!remoteConfigKey) {
      return content;
    }
    return <RemoteConfigGate configKey={remoteConfigKey}>{content}</RemoteConfigGate>;
  };

  return <Route exact={exact} location={location} path={path} render={renderRoute} />;
};

AuthRoute.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  path: PropTypes.string.isRequired,
  location: PropTypes.object,
  onOrgChangeRedirectTo: PropTypes.string.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.oneOf(Object.values(USER_ROLES))),
  exact: PropTypes.bool,
  remoteConfigKey: PropTypes.oneOf(Object.values(REMOTE_CONFIG_KEYS)),
  writeOnly: PropTypes.bool,
};

export default React.memo(AuthRoute);
